import { Lightbulb, QuestionCircle } from "@solar-icons/react/ssr";
import { Divider, useTheme, useMediaQuery } from "@mui/material";
import Grid from "@mui/material/Grid";
import Tooltip from "@mui/material/Tooltip";
import { useSelector } from "react-redux";
import styled from "styled-components";

import useEntitlements from "@/auth/useEntitlements";
import { FEATURE_VIEW_BID_GOALS } from "@/constants";
import { store } from "@/sheet/bidStore";
import { formattedCurrency } from "@/utils";

const Container = styled.div`
    background: ${(props) => props.theme.palette.background.paper};
    padding: ${(props) => (props.isMobile ? "24px" : "32px 46px")};
    box-sizing: border-box;
    border-radius: 20px;
`;
const Table = styled.table`
    border-collapse: collapse;
    font-size: 12px;

    th {
        text-align: left;
        padding: ${(props) =>
            props.isMobile ? "12px 12px 12px 0" : "12px 30px 12px 0"};
        width: 100%;

        span {
            display: inline-block;
            vertical-align: bottom;
            margin-left: 6px;
            width: 14px;
            height: 14px;
            border-radius: 50%;
            background-color: ${(props) => props.theme.palette.text.disabled};
            color: ${(props) => props.theme.palette.text.secondary};
            font-size: 12px;
            text-align: center;
        }
    }
    td {
        text-align: right;
        padding: ${(props) => (props.isMobile ? "12px" : "12px 30px")};
        white-space: nowrap;

        &.rightBorder {
            border-right: ${(props) =>
                `1px solid ${props.theme.palette.text.disabled}`};
        }

        &.warning {
            color: ${(props) => props.theme.palette.warning.main};
            cursor: help;
        }
        &.error {
            color: ${(props) => props.theme.palette.error.main};
            cursor: help;
        }
    }

    thead {
        th {
            padding-top: 0;
        }
        td {
            font-size: 12px;
            color: ${(props) => props.theme.palette.text.secondary};
            padding-top: 0;
        }
    }

    tbody {
        th {
            font-weight: normal;
            border-bottom: ${(props) =>
                `1px solid ${props.theme.palette.text.disabled}`};
        }
        td {
            border-bottom: ${(props) =>
                `1px solid ${props.theme.palette.text.disabled}`};
        }
    }

    tfoot {
        td {
            font-weight: bold;
        }
    }
`;
const TooltipTotalQuotedContainer = styled.div`
    color: ${(props) => props.theme.palette.text.primary};
    font-size: 12px;

    h3 {
        margin: 0 0 0 0;
        font-weight: 500;
        font-size: 12px;
    }

    h4 {
        margin: 0 0 0 0;
        font-weight: 400;
        font-size: 12px;
    }

    p {
        color: ${(props) => props.theme.palette.text.secondary};
        margin: 8px 0 0 0;
    }

    span {
        color: ${(props) => props.theme.palette.success.main};

        &.warning {
            color: ${(props) => props.theme.palette.warning.main};
        }
        &.error {
            color: ${(props) => props.theme.palette.error.main};
        }
    }

    .box {
        background-color: ${(props) => props.theme.palette.background.paper};
        padding: 12px 16px;
        margin-top: 18px;
        border-radius: 10px;
        max-width: 250px;
        width: fit-content;
    }
`;

const TooltipTh = ({ title, tooltip }) => (
    <th>
        {title}
        <Tooltip title={tooltip} placement="right">
            <span>
                <QuestionCircle size={10} />
            </span>
        </Tooltip>
    </th>
);

const LightBulbTh = (props) => (
    <div style={{ padding: 0, paddingRight: 5, ...props.style }}>
        <span>
            <Lightbulb size={10} />
        </span>
    </div>
);

const TooltipTotalQuoted = ({ theme, quoted, target, minimum, currency }) => {
    const difference = quoted - target;
    const label = difference < 0 ? "below" : "above";
    const severity =
        quoted < minimum ? "error" : quoted < target ? "warning" : "";

    return (
        <TooltipTotalQuotedContainer theme={theme} security={severity}>
            <h3>
                Total Quoted is{" "}
                <span className={severity}>{label} target </span>
            </h3>

            {severity && (
                <div className="box">
                    <Grid
                        container
                        spacing={2}
                        justifyContent={"space-between"}
                    >
                        <Grid item>
                            <h4 style={{ borderLeft: "1px grey solid" }}>
                                Target:{" "}
                                {formattedCurrency(target, currency, true)}
                            </h4>
                        </Grid>
                        <Grid item>
                            <Divider
                                orientation="vertical"
                                sx={{ height: "100%" }}
                            />{" "}
                        </Grid>
                        <Grid item>
                            <h4>
                                Minimum:{" "}
                                {formattedCurrency(minimum, currency, true)}
                            </h4>
                        </Grid>
                    </Grid>
                    <p>
                        Trying adding more markup to your External or Internal
                        cost if the bid is below Target or Minimum Quoted.
                    </p>
                </div>
            )}
        </TooltipTotalQuotedContainer>
    );
};
export default function BidInsights({ currency, isRunning }) {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("md"));

    const { hasFeature } = useEntitlements();
    const canViewBidGoals = hasFeature(FEATURE_VIEW_BID_GOALS);

    const insights = store.bid.insights;
    const totalFlag =
        canViewBidGoals &&
        insights.project_total_quoted < insights.minimum_quoted
            ? "error"
            : canViewBidGoals &&
                insights.project_total_quoted < insights.target_quoted
              ? "warning"
              : "";

    return (
        <Grid container rowSpacing={4} columnSpacing={isMobile ? 0 : 4}>
            <Grid item flexGrow={1}>
                <Container theme={theme} isMobile={isMobile}>
                    <Table theme={theme} isMobile={isMobile}>
                        <thead>
                            <tr>
                                <th>Quoted</th>
                                <td className="rightBorder">Current Bid</td>
                                <td>Project Total</td>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <TooltipTh
                                    title="External Quoted"
                                    tooltip='Sum of all lines Quoted w/ resource type "External"'
                                />
                                <td className="rightBorder">
                                    {formattedCurrency(
                                        insights.external_quoted,
                                        currency,
                                        true,
                                    )}
                                </td>
                                <td>
                                    {formattedCurrency(
                                        insights.project_external_quoted,
                                        currency,
                                        true,
                                    )}
                                </td>
                            </tr>
                            <tr>
                                <TooltipTh
                                    title="Internal Quoted"
                                    tooltip='Sum of all lines Quoted w/ resource type "Internal"'
                                />
                                <td className="rightBorder">
                                    {formattedCurrency(
                                        insights.internal_quoted,
                                        currency,
                                        true,
                                    )}
                                </td>
                                <td>
                                    {formattedCurrency(
                                        insights.project_internal_quoted,
                                        currency,
                                        true,
                                    )}
                                </td>
                            </tr>
                            <tr>
                                <TooltipTh
                                    title="Add-ons Quoted"
                                    tooltip='Sum of all lines Quoted as "Add-ons" (including insurance)'
                                />
                                <td className="rightBorder">
                                    {formattedCurrency(
                                        insights.addons_quoted,
                                        currency,
                                        true,
                                    )}
                                </td>
                                <td>
                                    {formattedCurrency(
                                        insights.project_addons_quoted,
                                        currency,
                                        true,
                                    )}
                                </td>
                            </tr>
                        </tbody>
                        <tfoot>
                            <tr>
                                <TooltipTh
                                    title="Total Quoted"
                                    tooltip="Sum of all the above"
                                />
                                <td className="rightBorder">
                                    {formattedCurrency(
                                        insights.total_quoted,
                                        currency,
                                        true,
                                    )}
                                </td>

                                {canViewBidGoals ? (
                                    <Tooltip
                                        title={
                                            <TooltipTotalQuoted
                                                theme={theme}
                                                quoted={
                                                    insights.project_total_quoted
                                                }
                                                target={insights.target_quoted}
                                                minimum={
                                                    insights.minimum_quoted
                                                }
                                                currency={currency}
                                            />
                                        }
                                        componentsProps={{
                                            tooltip: {
                                                sx: {
                                                    backgroundColor:
                                                        theme.palette.background
                                                            .default,
                                                    borderRadius: "20px",
                                                    padding: "22px",
                                                    boxShadow:
                                                        "0px 0px 50px 0px #0000001A",
                                                    maxWidth: "600px",
                                                },
                                            },
                                        }}
                                        placement="bottom"
                                    >
                                        <td className={totalFlag}>
                                            <Grid
                                                container
                                                justifyContent={"flex-end"}
                                                flexWrap={"nowrap"}
                                            >
                                                <Grid item>
                                                    <LightBulbTh />
                                                </Grid>
                                                <Grid item>
                                                    {formattedCurrency(
                                                        insights.project_total_quoted,
                                                        currency,
                                                        true,
                                                    )}
                                                </Grid>
                                            </Grid>
                                        </td>
                                    </Tooltip>
                                ) : (
                                    <td className={totalFlag}>
                                        {formattedCurrency(
                                            insights.project_total_quoted,
                                            currency,
                                            true,
                                        )}
                                    </td>
                                )}
                            </tr>
                        </tfoot>
                    </Table>
                </Container>
            </Grid>

            <Grid item flexGrow={1}>
                <Container theme={theme} isMobile={isMobile}>
                    <Table theme={theme} isMobile={isMobile}>
                        <thead>
                            <tr>
                                <th>
                                    {isRunning ? "Running " : ""}Profitability
                                </th>
                                <td className="rightBorder">Current Bid</td>
                                <td>Project Total</td>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <TooltipTh
                                    title={`${isRunning ? "Running " : ""}External Budget`}
                                    tooltip={`Sum of all lines ${isRunning ? "Running " : ""}Budget w/ resource type "External"`}
                                />
                                <td className="rightBorder">
                                    {formattedCurrency(
                                        isRunning
                                            ? insights.external_budget_running
                                            : insights.external_budget,
                                        currency,
                                        true,
                                    )}
                                </td>
                                <td>
                                    {formattedCurrency(
                                        isRunning
                                            ? insights.project_external_budget_running
                                            : insights.project_external_budget,
                                        currency,
                                        true,
                                    )}
                                </td>
                            </tr>
                            <tr>
                                <TooltipTh
                                    title={`${isRunning ? "Running " : ""}Gross Profit`}
                                    tooltip={`Total Quoted - ${isRunning ? "Running " : ""}External Budget`}
                                />
                                <td className="rightBorder">
                                    {formattedCurrency(
                                        isRunning
                                            ? insights.gross_profit_running
                                            : insights.gross_profit,
                                        currency,
                                        true,
                                    )}
                                </td>
                                <td>
                                    {formattedCurrency(
                                        isRunning
                                            ? insights.project_gross_profit_running
                                            : insights.project_gross_profit,
                                        currency,
                                        true,
                                    )}
                                </td>
                            </tr>
                            <tr>
                                <TooltipTh
                                    title={`${isRunning ? "Running " : ""}Labour & Overhead`}
                                    tooltip={`Total ${isRunning ? "Running " : ""}Internal Cost + 2% of External Quoted`}
                                />
                                <td className="rightBorder">
                                    {formattedCurrency(
                                        isRunning
                                            ? insights.overhead_running
                                            : insights.overhead,
                                        currency,
                                        true,
                                    )}
                                </td>
                                <td>
                                    {formattedCurrency(
                                        isRunning
                                            ? insights.project_overhead_running
                                            : insights.project_overhead,
                                        currency,
                                        true,
                                    )}
                                </td>
                            </tr>
                        </tbody>
                        <tfoot>
                            <tr>
                                <TooltipTh
                                    title={`${isRunning ? "Running " : ""}Net Profit`}
                                    tooltip={`${isRunning ? "Running " : ""}Gross Profit - ${isRunning ? "Running " : ""}Labour & Overhead`}
                                />
                                <td className="rightBorder">
                                    {formattedCurrency(
                                        isRunning
                                            ? insights.net_profit_running
                                            : insights.net_profit,
                                        currency,
                                        true,
                                    )}
                                </td>
                                <td>
                                    {formattedCurrency(
                                        isRunning
                                            ? insights.project_net_profit_running
                                            : insights.project_net_profit,
                                        currency,
                                        true,
                                    )}
                                </td>
                            </tr>
                        </tfoot>
                    </Table>
                </Container>
            </Grid>
        </Grid>
    );
}
